/**
 * API Client — общение Mini App с Bot Worker
 *
 * Все запросы идут через initData для авторизации.
 * Base URL настраивается через env (VITE_API_URL).
 */

import { 
  ok, err, 
  type Result, 
  type BotConfig, 
  type AIAgent, 
  type AIAgentTier, 
  type AIProviderType, 
  type PlatformLimits, 
  type SubscriptionPlan 
} from '@tg-miniapp/shared-types';
import { useAppSettings } from '../stores/appSettings';
import { safeLocalStorage } from './storage';

// ─── Config ────────────────────────────────────────────────

export const getApiBase = (): string => {
  let url = 'https://api.biz-klient.ru';
  if (import.meta.env.VITE_API_URL) {
    url = import.meta.env.VITE_API_URL as string;
  } else if (typeof window !== 'undefined' && window.location.origin && window.location.origin.includes('biz-klient.ru')) {
    url = window.location.origin;
  }
  return url.trim().replace(/\/+$/, '');
};

export const API_BASE = getApiBase();

// ─── Core Request ──────────────────────────────────────────

interface ApiOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  body?: unknown;
  initData?: string;
  businessId?: string;
}

async function apiRequest<T>(
  path: string,
  options: ApiOptions = {},
): Promise<Result<T, Error>> {
  const { method = 'GET', body, initData, businessId: customBusinessId } = options;

  const cacheKey = method === 'GET' ? `api_cache:${path}` : null;

  const baseUrl = getApiBase();

  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (initData) {
      const cleanInitData = initData.replace(/[\r\n]+/g, '').trim();
      const safeInitData = /[\u0080-\uFFFF]/.test(cleanInitData) ? encodeURIComponent(cleanInitData) : cleanInitData;
      headers['Authorization'] = `tma ${safeInitData}`;
    }

    const businessId = customBusinessId || useAppSettings.getState().currentBusinessId;
    if (businessId && !path.startsWith('/api/platform/') && !path.startsWith('/api/admin/')) {
      headers['X-Business-Id'] = businessId;
    }

    const currentRole = useAppSettings.getState().currentRole;
    if (currentRole && !path.startsWith('/api/platform/') && !path.startsWith('/api/admin/')) {
      headers['X-Active-Role'] = currentRole;
    }

    const sandboxMockUserId = useAppSettings.getState().sandboxMockUserId;
    if (sandboxMockUserId && !path.startsWith('/api/platform/') && !path.startsWith('/api/admin/')) {
      headers['X-Sandbox-Mock-User-Id'] = sandboxMockUserId;
    }

    let response: Response;
    try {
      response = await fetch(`${baseUrl}${path}`, {
        method,
        headers,
        body: body ? JSON.stringify(body) : undefined,
      });
    } catch (fetchErr) {
      // Автоматический фолбэк на Cloudflare Worker, если первичный хост VPS (api.biz-klient.ru) недоступен
      const cfWorkerFallback = 'https://tg-miniapp-bot.bovad72.workers.dev';
      const isStaticHosting = typeof window !== 'undefined' && (window.location.origin.includes('pages.dev') || window.location.origin.includes('github.io'));
      
      if (baseUrl !== cfWorkerFallback) {
        try {
          response = await fetch(`${cfWorkerFallback}${path}`, {
            method,
            headers,
            body: body ? JSON.stringify(body) : undefined,
          });
        } catch {
          if (typeof window !== 'undefined' && window.location.origin && baseUrl !== window.location.origin && method === 'GET' && !isStaticHosting) {
            try {
              response = await fetch(`${window.location.origin}${path}`, {
                method,
                headers,
                body: body ? JSON.stringify(body) : undefined,
              });
            } catch {
              throw fetchErr;
            }
          } else {
            throw fetchErr;
          }
        }
      } else {
        throw fetchErr;
      }
    }

    if (!response.ok) {
      const errorText = await response.text();
      let errorMsg = `API error ${response.status}`;
      try {
        const json = JSON.parse(errorText);
        if (json.error) errorMsg = json.error;
      } catch {
        if (errorText) errorMsg += `: ${errorText}`;
      }
      if (path !== '/api/platform/logs/miniapp') {
        const activeInitData = initData || (typeof window !== 'undefined' ? window.Telegram?.WebApp?.initData : '');
        if (activeInitData) {
          logMiniAppEvent(activeInitData, 'error', `[API Error ${response.status}] ${path}`, errorMsg).catch(() => {});
        }
      }
      return err(new Error(errorMsg));
    }

    const data = await response.json() as T;

    if (cacheKey) {
      try {
        safeLocalStorage.setItem(cacheKey, JSON.stringify({ data, ts: Date.now() }));
      } catch { /* quota exceeded — ignore */ }
    }

    return ok(data);
  } catch (error: unknown) {
    if (cacheKey) {
      try {
        const cached = safeLocalStorage.getItem(cacheKey);
        if (cached) {
          const { data } = JSON.parse(cached) as { data: T; ts: number };
          return ok(data);
        }
      } catch { /* parse error — fall through */ }
    }
    const errMsg = error instanceof Error ? error.message : String(error);
    console.error(`[API Client Error] Request to ${path} failed:`, error);
    if (path !== '/api/platform/logs/miniapp') {
      const activeInitData = initData || (typeof window !== 'undefined' ? window.Telegram?.WebApp?.initData : '');
      if (activeInitData) {
        logMiniAppEvent(activeInitData, 'error', `[Network Failure] ${path}`, errMsg).catch(() => {});
      }
    }
    if (errMsg.includes('Failed to fetch')) {
      return err(new Error(`Не удалось связаться с сервером API (${baseUrl}${path}). Проверьте HTTPS и доступность сервера.`));
    }
    return err(new Error(`Network error: ${errMsg}`));
  }
}

interface UserProfile {
  userId: string;
  role: 'owner' | 'manager' | 'assistant' | 'master' | 'admin' | 'client';
  businessId: string | null;
  workerId: string | null;
  isAdmin: boolean;
  isRealAdmin?: boolean;
}

interface BotInfo {
  ok: boolean;
  bot: { id: number; first_name: string; username: string };
  profile: UserProfile;
}

export async function getBotInfo(initData: string): Promise<Result<BotInfo, Error>> {
  return apiRequest<BotInfo>('/api/me', { initData });
}

export interface ApiUserMembership {
  businessId: string;
  businessName: string;
  role: 'owner' | 'manager' | 'assistant' | 'master' | 'admin' | 'client';
  workerId: string | null;
  permissions: any;
}

interface MembershipsResponse {
  ok: boolean;
  memberships: ApiUserMembership[];
}

export async function getUserMemberships(initData: string): Promise<Result<MembershipsResponse, Error>> {
  return apiRequest<MembershipsResponse>('/api/me/memberships', { initData });
}

// ─── Health ────────────────────────────────────────────────

interface HealthResponse {
  status: string;
  version: string;
  timestamp: number;
}

export async function getHealth(): Promise<Result<HealthResponse, Error>> {
  return apiRequest<HealthResponse>('/health');
}

// ─── Bookings ──────────────────────────────────────────────

interface BookingsResponse {
  bookings: Array<{
    id: string;
    clientTgId: string;
    slotStart: number;
    slotEnd: number;
    status: string;
    serviceName?: string;
  }>;
}

export async function getBookings(
  initData: string,
  date: string,
): Promise<Result<BookingsResponse, Error>> {
  return apiRequest<BookingsResponse>(`/api/bookings?date=${date}`, { initData });
}

// ─── Config ────────────────────────────────────────────────

export async function getBotConfig(
  initData: string,
  businessId?: string,
): Promise<Result<BotConfig, Error>> {
  return apiRequest<BotConfig>('/api/config', { initData, businessId });
}

export async function updateBotConfig(
  initData: string,
  config: BotConfig,
): Promise<Result<void, Error>> {
  return apiRequest<void>('/api/config', {
    method: 'POST',
    body: config,
    initData,
  });
}

export async function getPlatformSettings(initData: string) {
  return apiRequest<any>('/api/platform/settings', { initData });
}

export async function updatePlatformSettings(initData: string, settings: any) {
  return apiRequest<void>('/api/platform/settings', {
    method: 'PUT',
    body: settings,
    initData,
  });
}

// ─── Staff ──────────────────────────────────────────────────

export interface StaffMember {
  id: string;
  name: string;
  role: string;
  permissions: any;
  isActive: boolean;
  tgUserId?: string;
  dateHired?: string;
}

export async function getStaffList(initData: string) {
  return apiRequest<{ staff: StaffMember[] }>('/api/staff', { initData });
}

export async function addStaffMember(initData: string, name: string, role: string, tgUserId?: string) {
  return apiRequest<{ member: StaffMember }>('/api/staff', {
    method: 'POST',
    body: { name, role, tgUserId },
    initData
  });
}

export async function updateStaffMember(initData: string, memberId: string, updates: Partial<StaffMember>) {
  return apiRequest<{ member: StaffMember }>(`/api/staff/${memberId}`, {
    method: 'PUT',
    body: updates,
    initData
  });
}

export async function deleteStaffMember(initData: string, memberId: string) {
  return apiRequest<{ ok: boolean }>(`/api/staff/${memberId}`, {
    method: 'DELETE',
    initData
  });
}

export async function updateStaffSchedule(initData: string, schedule: Record<string, 'work' | 'off' | 'vacation'>) {
  return apiRequest<{ ok: boolean; schedule: Record<string, 'work' | 'off' | 'vacation'> }>('/api/staff/me/schedule', {
    method: 'PUT',
    body: { schedule },
    initData
  });
}

export async function getStaffAnalytics(initData: string, workerId: string) {
  return apiRequest<{
    stats: {
      rating: number;
      revenue: number;
      revenueTrend?: string;
      services: number;
      completionRate: number;
      shifts: number;
      attendanceRate?: number;
      dateHired: string;
      tenureDate: string;
      upcomingDay: number;
      upcomingWeek: number;
      upcomingMonth: number;
      uniqueClients?: number;
      retentionRate: number;
    }
  }>(`/api/staff/${workerId}/analytics`, { initData });
}
export async function createStaffInvite(initData: string, role: string, permissions: any) {
  return apiRequest<{ code: string; inviteLink: string }>('/api/business/invite', {
    method: 'POST',
    body: { role, permissions },
    initData
  });
}

// ─── Billing ────────────────────────────────────────────────

export async function getBillingStatus(initData: string) {
  return apiRequest<{
    plan: SubscriptionPlan;
    expiresAt: number | null;
    autoRenew: boolean;
    isGlobalMarketingPro?: boolean;
    activeProviders?: Array<{ id: string; name: string; isEnabled: boolean }>;
  }>('/api/billing/status', { initData });
}

export async function payBilling(
  initData: string, 
  plan: SubscriptionPlan, 
  method: 'stripe' | 'robokassa' | 'yookassa' | 'cloudpayments' | 'card' | 'sbp' | 'stars',
  promoCode?: string
) {
  return apiRequest<{ 
    ok: boolean; 
    status?: { plan: SubscriptionPlan; expiresAt: number | null }; 
    stripeUrl?: string; 
    robokassaUrl?: string;
    yookassaUrl?: string;
    cpWidget?: {
      publicId: string;
      amount: number;
      currency: string;
      description: string;
      accountId: string;
      data: any;
    };
  }>('/api/billing/pay', {
    method: 'POST',
    body: { plan, method, promoCode },
    initData
  });
}

export interface BillingHistoryItem {
  id: string;
  businessId: string;
  plan: SubscriptionPlan;
  amount: number;
  date: number;
}

export async function getBillingHistory(initData: string) {
  return apiRequest<{ history: BillingHistoryItem[] }>('/api/billing/history', { initData });
}

export async function cancelBilling(initData: string) {
  return apiRequest<{ ok: boolean; plan: SubscriptionPlan; expiresAt: number | null }>('/api/billing/cancel', {
    method: 'POST',
    initData
  });
}

export async function requestBillingRefund(initData: string, paymentId: string, reason: string) {
  return apiRequest<{ ok: boolean; id: string }>('/api/billing/refund-request', {
    method: 'POST',
    body: { paymentId, reason },
    initData
  });
}

export async function toggleAutoRenewBilling(initData: string, autoRenew: boolean) {
  return apiRequest<{ ok: boolean; autoRenew: boolean }>('/api/billing/auto-renew', {
    method: 'POST',
    body: { autoRenew },
    initData
  });
}

export interface PlatformPayment {
  id: string;
  order_id: string;
  provider: string;
  provider_payment_id: string;
  amount: number;
  currency: string;
  status: string;
  payment_method: string;
  masked_pan?: string;
  is_recurrent: number;
  created_at: number;
  business_id?: string;
  plan?: string;
}

export async function getPlatformPayments(initData: string) {
  return apiRequest<{ payments: PlatformPayment[] }>('/api/platform/payments', { initData });
}

export async function refundPlatformPayment(initData: string, paymentId: string) {
  return apiRequest<{ ok: boolean }>(`/api/platform/payments/${paymentId}/refund`, {
    method: 'POST',
    initData
  });
}

export interface RiskDashboardData {
  metrics: {
    totalPayments: number;
    paidPayments: number;
    refundedPayments: number;
    totalVolume: number;
    refundedVolume: number;
    refundRate: number;
    averageTicket: number;
    last24hVolume: number;
    avgDailyVolume: number;
  };
  anomalies: {
    isVolumeSpike: boolean;
    isRefundRateHigh: boolean;
  };
  warnings: string[];
  status: 'stable' | 'risky';
}

export async function getPlatformRiskDashboard(initData: string) {
  return apiRequest<RiskDashboardData>('/api/platform/risk-dashboard', { initData });
}

export async function downloadPlatformPaymentEvidence(initData: string, paymentId: string): Promise<boolean> {
  try {
    const headers: Record<string, string> = {
      'Authorization': `tma ${initData}`
    };
    const response = await fetch(`${getApiBase()}/api/platform/payments/${paymentId}/evidence`, {
      method: 'GET',
      headers
    });
    if (!response.ok) return false;
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `evidence_bundle_${paymentId}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
    return true;
  } catch {
    return false;
  }
}

// ─── AI Agents (Platform) ────────────────────────────────────

export async function getAIAgents(initData: string) {
  return apiRequest<{ agents: AIAgent[] }>('/api/platform/ai-agents', { initData });
}

export async function createAIAgent(initData: string, agent: Omit<AIAgent, 'id'>) {
  return apiRequest<{ agent: AIAgent }>('/api/platform/ai-agents', {
    method: 'POST',
    body: agent,
    initData
  });
}

export async function updateAIAgent(initData: string, id: string, updates: Partial<AIAgent>) {
  return apiRequest<{ agent: AIAgent }>(`/api/platform/ai-agents/${id}`, {
    method: 'PUT',
    body: updates,
    initData
  });
}


export async function deleteAIAgent(initData: string, id: string) {
  return apiRequest<{ ok: boolean }>(`/api/platform/ai-agents/${id}`, {
    method: 'DELETE',
    initData
  });
}

// ─── Platform Limits ─────────────────────────────────────────

export async function getPlatformLimits(initData: string) {
  return apiRequest<{ limits: PlatformLimits }>('/api/platform/limits', { initData });
}


// ─── Platform Businesses ─────────────────────────────────────

export interface BusinessAdminView {
  id: string;
  name: string;
  business: string;
  sub: SubscriptionPlan;
  expires: number | null;
  status: 'active' | 'blocked';
  mrr: number;
  config?: any;
}

export async function getBusinessesList(initData: string) {
  return apiRequest<{ businesses: BusinessAdminView[] }>('/api/platform/businesses', { initData });
}

export async function updateBusiness(initData: string, id: string, updates: any) {
  return apiRequest<{ ok: boolean }>(`/api/platform/businesses/${id}`, {
    method: 'PUT',
    body: updates,
    initData
  });
}

export interface PlatformMetrics {
  mrr: number;
  totalBots: number;
  activeBots: number;
  payingBots: number;
  conversion: number;
  activity: number[];
  funnel?: {
    starts: number;
    created: number;
    services: number;
    ai: number;
    paid: number;
  };
}

export async function getPlatformMetrics(initData: string) {
  return apiRequest<{ metrics: PlatformMetrics }>('/api/platform/metrics', { initData });
}

export interface PlatformLog {
  id: string;
  type: 'payment' | 'cancel_payment' | 'refund' | 'error' | 'system' | 'miniapp';
  level?: 'info' | 'error';
  msg: string;
  date: string;
  amount: string;
  details?: string;
}

export async function getPlatformLogs(initData: string) {
  return apiRequest<{ logs: PlatformLog[] }>('/api/platform/logs', { initData });
}

export async function getMiniAppLogsSettings(initData: string) {
  return apiRequest<{ retentionDays: number; errorRetentionDays: number }>('/api/platform/logs/miniapp/settings', { initData });
}

export async function updateMiniAppLogsSettings(initData: string, retentionDays: number, errorRetentionDays: number) {
  return apiRequest<{ ok: boolean }>('/api/platform/logs/miniapp/settings', {
    method: 'PUT',
    body: { retentionDays, errorRetentionDays },
    initData
  });
}

export async function deleteMiniAppLogs(initData: string, mode: 'all' | 'non-error' | 'error-only') {
  return apiRequest<{ ok: boolean }>(`/api/platform/logs/miniapp?mode=${mode}`, {
    method: 'DELETE',
    initData
  });
}

export async function logMiniAppEvent(initData: string, level: 'info' | 'error', msg: string, details?: string) {
  return apiRequest<{ ok: boolean }>('/api/platform/logs/miniapp', {
    method: 'POST',
    body: { level, msg, details },
    initData
  });
}


export interface BackupItem {
  id: string;
  filename: string;
  sizeBytes: number;
  createdAt: string;
  status: 'success' | 'failed' | 'restoring';
}

export async function getBackups(initData: string) {
  return apiRequest<{ backups: BackupItem[] }>('/api/platform/backups', { initData });
}

export async function createBackup(initData: string, type: 'manual' | 'auto') {
  return apiRequest<{ ok: boolean; backup: BackupItem }>('/api/platform/backups', {
    method: 'POST',
    body: { type },
    initData
  });
}

export async function restoreBackup(initData: string, id: string) {
  return apiRequest<{ ok: boolean }>('/api/platform/backups/restore', {
    method: 'POST',
    body: { id },
    initData
  });
}

export async function deleteBackup(initData: string, id: string) {
  return apiRequest<{ ok: boolean }>(`/api/platform/backups/${id}`, {
    method: 'DELETE',
    initData
  });
}

export interface FeatureFlag {
  key: string;
  name: string;
  description: string;
  isEnabled: boolean;
  canaryPercentage: number;
  allowedRoles: string[];
  betaOnly: boolean;
}

export async function getFeatureFlags(initData: string) {
  return apiRequest<{ flags: FeatureFlag[] }>('/api/platform/feature-flags', { initData });
}

export async function updateFeatureFlags(initData: string, flags: FeatureFlag[]) {
  return apiRequest<{ ok: boolean; flags: FeatureFlag[] }>('/api/platform/feature-flags', {
    method: 'POST',
    body: { flags },
    initData
  });
}

export async function generateFaq(initData: string, prompt: string) {
  return apiRequest<{ ok: boolean; faq: Array<{ question: string; answer: string }> }>('/api/business/faq/generate', {
    method: 'POST',
    body: { prompt },
    initData
  });
}

export interface MarketingCampaign {
  subject: string;
  body: string;
  cta: string;
}

export async function generateCampaign(initData: string, payload: { segment: string; tone: string; description: string }) {
  return apiRequest<{ ok: boolean; campaign: MarketingCampaign }>('/api/business/marketing/generate', {
    method: 'POST',
    body: payload,
    initData
  });
}

export interface DynamicPricingRule {
  dayOfWeek: string;
  startHour: string;
  endHour: string;
  discountPercent: number;
  reason: string;
}

export interface PricingAnalysisResult {
  explanation: string;
  rules: DynamicPricingRule[];
}

export async function analyzePricing(initData: string) {
  return apiRequest<{ ok: boolean; analysis: PricingAnalysisResult; occupancyStats: any }>('/api/business/pricing/analyze', {
    method: 'POST',
    initData
  });
}

export async function updatePlatformLimits(initData: string, limits: any) {
  return apiRequest<{ ok: boolean }>('/api/platform/limits', { 
    initData, 
    method: 'POST', 
    body: limits 
  });
}

export async function getPromoCodes(initData: string) {
  return apiRequest<{ codes: any[] }>('/api/platform/promo', { initData });
}

export async function createPromoCode(initData: string, data: any) {
  return apiRequest<{ ok: boolean }>('/api/platform/promo', { 
    initData, 
    method: 'POST', 
    body: data 
  });
}

export async function deletePromoCode(initData: string, code: string) {
  return apiRequest<{ ok: boolean }>(`/api/platform/promo/${code}`, {
    initData,
    method: 'DELETE'
  });
}

export async function getPlatformFinance(initData: string) {
  return apiRequest<{ payments: any[], stats: any }>('/api/platform/finance', { initData });
}

export async function deleteBusiness(initData: string, id: string) {
  return apiRequest<{ ok: boolean }>(`/api/platform/businesses/${id}`, {
    method: 'DELETE',
    initData
  });
}

export async function getPlatformPaymentSettings(initData: string) {
  return apiRequest<{ settings: any }>('/api/platform/payment-settings', { initData });
}

export async function updatePlatformPaymentSettings(initData: string, settings: any) {
  return apiRequest<{ ok: boolean }>('/api/platform/payment-settings', {
    method: 'POST',
    body: settings,
    initData
  });
}

export async function getPlatformBillingProviders(initData: string) {
  return apiRequest<{ providers: any[] }>('/api/platform/billing-providers', { initData });
}

export async function updatePlatformBillingProviders(initData: string, providers: any[]) {
  return apiRequest<{ ok: boolean }>('/api/platform/billing-providers', {
    method: 'POST',
    body: providers,
    initData
  });
}

export async function getPlatformMonitoringMetrics(initData: string) {
  return apiRequest<any>('/api/monitoring/metrics', { initData });
}

export async function getPlatformHealthDashboard(initData: string) {
  return apiRequest<any>('/api/monitoring/health-dashboard', { initData });
}

export async function replyToFeedback(initData: string, id: string, replyText: string) {
  return apiRequest<{ ok: boolean }>(`/api/admin/feedback/${id}/reply`, {
    method: 'POST',
    body: { replyText },
    initData
  });
}

export async function getBusinessDashboardData(initData: string) {
  return apiRequest<{ stats: any }>('/api/business/dashboard-data', { initData });
}

export async function getBookingsByDate(initData: string, date: string) {
  return apiRequest<{ bookings: any[] }>(`/api/business/bookings?date=${date}`, { initData });
}

export interface AllOwnerBookingsResponse {
  ok: boolean;
  businesses: Record<string, {
    businessName: string;
    bookings: any[];
  }>;
}

export async function getAllOwnerBookings(initData: string, date: string) {
  return apiRequest<AllOwnerBookingsResponse>(`/api/business/all-bookings?date=${date}`, { initData });
}

export async function updateBookingStatus(initData: string, id: string, status: string) {
  return apiRequest<{ ok: boolean }>(`/api/business/bookings/${id}`, {
    method: 'PATCH',
    body: { status },
    initData
  });
}

// ─── Reviews ────────────────────────────────────────────────

export interface UIReview {
  id: string;
  author: string;
  rating: number;
  text: string;
  date: string;
  master: string;
  reply: string | null;
}

export async function getReviews(initData: string) {
  return apiRequest<{ reviews: UIReview[] }>('/api/business/reviews', { initData });
}

export async function replyToReview(initData: string, id: string, reply: string) {
  return apiRequest<{ ok: boolean; review: UIReview }>(`/api/business/reviews/${id}/reply`, {
    method: 'POST',
    body: { reply },
    initData
  });
}

export interface ReviewsAnalysis {
  sentiment: { positive: number; neutral: number; negative: number };
  complaints: string[];
  compliments: string[];
  recommendations: string[];
}

export async function analyzeReviews(initData: string) {
  return apiRequest<{ ok: boolean; analysis: ReviewsAnalysis }>('/api/business/reviews/analyze', {
    method: 'POST',
    initData
  });
}

// ─── B2C Client Endpoints ────────────────────────────────────

export async function getClientMasters(initData: string) {
  return apiRequest<any[]>('/api/client/masters', { initData });
}

export async function getClientSlots(initData: string, workerId: string, date: string, durationMinutes: number) {
  return apiRequest<string[]>(`/api/client/slots?workerId=${workerId}&date=${date}&duration=${durationMinutes}`, { initData });
}

export async function getClientLoyalty(initData: string) {
  return apiRequest<{ points: number; tier: 'silver' | 'gold' | 'platinum'; discountPercent: number }>('/api/client/loyalty', { initData });
}

export async function getClientLoyaltyHistory(initData: string) {
  return apiRequest<any[]>('/api/client/loyalty/history', { initData });
}

export interface CrossSellRecommendation {
  serviceId: string;
  service: any;
  text: string;
  discountPercent: number;
  discountedPrice: number;
}

export async function getClientCrossSell(initData: string, currentServiceIds: string[]) {
  return apiRequest<{ ok: boolean; recommendation: CrossSellRecommendation | null }>('/api/client/cross-sell', {
    method: 'POST',
    body: { currentServiceIds },
    initData
  });
}

export async function getClientHistory(initData: string) {
  return apiRequest<any[]>('/api/client/history', { initData });
}

export async function createBooking(initData: string, data: {
  serviceIds: string[];
  workerId?: string;
  date: string;
  time: string;
  usePoints: boolean;
  acceptedCrossSellId?: string;
  clientName?: string;
  clientTgUsername?: string;
  clientPhone?: string;
  phoneConsentGranted?: boolean;
}) {
  return apiRequest<{ ok: boolean; booking: any }>('/api/client/bookings', {
    method: 'POST',
    body: data,
    initData
  });
}

export async function cancelBooking(initData: string, id: string, reason: string) {
  return apiRequest<{ ok: boolean }>(`/api/client/bookings/${id}/cancel`, {
    method: 'POST',
    body: { reason },
    initData
  });
}

export async function postClientChat(
  initData: string,
  message: string,
  history: Array<{ role: 'user' | 'assistant'; content: string }>
) {
  return apiRequest<{ content: string }>('/api/client/chat', {
    method: 'POST',
    body: { message, history },
    initData
  });
}


export async function setupBusiness(initData: string, data: {
  businessName: string;
  services: Array<{ name: string; price: number; durationMinutes: number }>;
}) {
  return apiRequest<{ ok: boolean; businessId: string; businessName: string }>('/api/business/setup', {
    method: 'POST',
    body: data,
    initData
  });
}

export async function acceptStaffInvite(initData: string, data: {
  code: string;
  name: string;
}) {
  return apiRequest<{ ok: boolean; businessId: string; role: string }>('/api/business/invite/accept', {
    method: 'POST',
    body: data,
    initData
  });
}

// ─── Universal Sandbox (Admin Only) ────────────────────────

export interface SandboxGenerateRequest {
  businessCount: number;
  businessesPerOwner?: number; // Количество бизнесов у одного владельца
  managersPerBusiness: number;
  assistantsPerBusiness: number;
  mastersPerBusiness: number;
  clientsPerBusiness: number;
  crossConnections?: Array<{ userTgId: string; businessId: string; role: string }>;
  ownerNameTemplate?: string;
  managerNameTemplate?: string;
  assistantNameTemplate?: string;
  masterNameTemplate?: string;
  clientNameTemplate?: string;
}

export interface SandboxState {
  businessCount: number;
  businessesPerOwner?: number;
  managersPerBusiness: number;
  assistantsPerBusiness: number;
  mastersPerBusiness: number;
  clientsPerBusiness: number;
  crossConnections: Array<{ userTgId: string; businessId: string; role: string }>;
  ownerNameTemplate?: string;
  managerNameTemplate?: string;
  assistantNameTemplate?: string;
  masterNameTemplate?: string;
  clientNameTemplate?: string;
  generatedAt: number;
}

export interface SandboxStatusResponse {
  active: boolean;
  state?: SandboxState;
  businesses?: Array<{ id: string; owner_tg_id: string; name: string; created_at: number }>;
  roles?: Array<{ user_tg_id: string; business_id: string; role: string; worker_id: string | null }>;
}

export async function generateSandbox(initData: string, data: SandboxGenerateRequest) {
  return apiRequest<{ ok: boolean; state: SandboxState }>('/api/admin/sandbox/generate', {
    method: 'POST',
    body: data,
    initData
  });
}

export async function getSandboxStatus(initData: string) {
  return apiRequest<SandboxStatusResponse>('/api/admin/sandbox/status', { initData });
}

export interface FeedbackItem {
  id: string;
  businessId: string;
  businessName: string;
  ownerName: string;
  ownerTgId: string;
  type: 'wish' | 'bug' | 'remark';
  text: string;
  status: 'active' | 'archived';
  createdAt: number;
  reply?: string;
  repliedAt?: number;
}

export async function submitFeedback(initData: string, type: 'wish' | 'bug' | 'remark', text: string) {
  return apiRequest<{ ok: boolean }>('/api/owner/feedback', {
    method: 'POST',
    body: { type, text },
    initData
  });
}

export async function getOwnerFeedbackList(initData: string) {
  return apiRequest<{ feedback: FeedbackItem[] }>('/api/owner/feedback', { initData });
}

export async function getFeedbackList(initData: string) {
  return apiRequest<{ feedback: FeedbackItem[] }>('/api/admin/feedback', { initData });
}

export async function deleteFeedback(initData: string, id: string) {
  return apiRequest<{ ok: boolean }>(`/api/admin/feedback/${id}`, {
    method: 'DELETE',
    initData
  });
}

export async function saveUserTimezone(initData: string, timezone: string) {
  return apiRequest<{ ok: boolean }>('/api/client/timezone', {
    method: 'POST',
    body: { timezone },
    initData
  });
}

export async function applyBillingPromo(initData: string, code: string, plan?: string) {
  return apiRequest<{
    ok: boolean;
    requiresPayment?: boolean;
    discountedPrice?: number;
    discount?: number;
    type?: string;
    targetPlan?: string;
    code?: string;
    message?: string;
    status?: any;
  }>('/api/billing/promo', {
    method: 'POST',
    body: { code, plan },
    initData
  });
}

export async function generateAIReply(initData: string, reviewId: string) {
  return apiRequest<{ ok: boolean; generatedReply: string }>(`/api/business/reviews/${reviewId}/generate-reply`, {
    method: 'POST',
    initData
  });
}

export async function generateAIWelcome(initData: string) {
  return apiRequest<{ ok: boolean; welcomeMessage: string }>('/api/business/ai/generate-welcome', {
    method: 'POST',
    initData
  });
}

export async function getCrmClients(initData: string) {
  return apiRequest<{ ok: boolean; clients: any[] }>('/api/business/crm/clients', { initData });
}

export async function broadcastCrmCampaign(
  initData: string,
  payload: { segment: string; messageText: string; buttonText?: string; buttonUrl?: string }
) {
  return apiRequest<{
    ok: boolean;
    totalTargeted: number;
    sentCount: number;
    errorCount: number;
    segment: string;
  }>('/api/business/crm/broadcast', {
    method: 'POST',
    body: payload,
    initData
  });
}


